import type { ReactNode } from "react";
import {
  DisclosureHeader,
  type useDisclosure,
} from "@/components/ui/Disclosure";
import { Panel } from "@/components/ui/Panel";
import { cn } from "@/lib/utils";

/**
 * A card whose heading folds what it holds: a claim form and its claims, the
 * positions waiting to be placed. The fold is the caller's, so a card can open
 * itself when something lands in it or close itself when its work is done.
 */
export function FoldingCard({
  fold,
  title,
  meta,
  lead,
  testId,
  className,
  bodyClassName,
  cardRef,
  children,
}: {
  fold: ReturnType<typeof useDisclosure>;
  /** Where a drag attaches, for a card that can be carried or dropped on. */
  cardRef?: (element: Element | null) => void;
  title: ReactNode;
  meta?: ReactNode;
  lead?: ReactNode;
  testId?: string;
  /** Where the card sits, and an edge tint for a card that says how something is doing. */
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <Panel
      as="section"
      shape="row"
      pad="none"
      ref={cardRef}
      data-testid={testId}
      className={className}
    >
      <DisclosureHeader
        trigger={fold.trigger}
        open={fold.open}
        title={title}
        meta={meta}
        lead={lead}
        className="px-4 py-3.5"
      />
      <div
        {...fold.panel}
        className={cn("border-t border-border-subtle px-4 pb-4", bodyClassName)}
      >
        {children}
      </div>
    </Panel>
  );
}
